// ==============================
// IEPQuantumExperience.jsx - Complete Enterprise Transformation Platform
// Version: Quantum 10.0.0 - Production Ready with IEPMasterData Integration
// ==============================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import IEPMasterData from './IEPMasterData'; // Import the master data

// ============ THEME MANAGEMENT SYSTEM ============
const ThemeContext = React.createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('quantum-theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('quantum-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ============ UTILITY FUNCTIONS ============
const calculateTransformationReadiness = (transformation, organizationalState) => {
  if (!transformation || !organizationalState) return { score: 0, dependenciesMet: false };
  
  const transformationData = IEPMasterData.transformations[transformation.id];
  if (!transformationData) return { score: 0, dependenciesMet: false };
  
  let score = 0;
  let maxScore = 100;
  
  // Check prerequisites (40% weight)
  const prerequisites = transformationData.prerequisites || [];
  const capabilities = organizationalState.capabilities || [];
  const prerequisitesMet = prerequisites.filter(p => capabilities.includes(p)).length;
  const prerequisiteScore = prerequisites.length > 0 ? (prerequisitesMet / prerequisites.length) * 40 : 40;
  score += prerequisiteScore;
  
  // Check dependencies (30% weight)
  const dependencies = transformationData.dependencies || [];
  const completed = organizationalState.completedTransformations || [];
  const dependenciesMet = dependencies.filter(d => completed.includes(d)).length;
  const dependencyScore = dependencies.length > 0 ? (dependenciesMet / dependencies.length) * 30 : 30;
  score += dependencyScore;
  
  // Organizational readiness (30% weight)
  const cultureScore = organizationalState.cultureScore || 0.5;
  const resourceScore = organizationalState.resourceScore || 0.5;
  const digitalMaturity = organizationalState.digitalMaturity || 0.5;
  const organizationalScore = ((cultureScore + resourceScore + digitalMaturity) / 3) * 30;
  score += organizationalScore;
  
  return {
    score: Math.round(Math.min(100, Math.max(0, score))),
    dependenciesMet: dependencies.length === 0 || dependenciesMet === dependencies.length,
    components: {
      prerequisites: Math.round(prerequisiteScore),
      dependencies: Math.round(dependencyScore),
      organizational: Math.round(organizationalScore)
    }
  };
};

const calculateOrganizationalReadiness = (organizationalState) => {
  if (!organizationalState) return { score: 0, components: {} };
  
  const capabilities = organizationalState.capabilities || [];
  const cultureScore = organizationalState.cultureScore || 0.5;
  const resourceScore = organizationalState.resourceScore || 0.5;
  const digitalMaturity = organizationalState.digitalMaturity || 0.5;
  
  const capabilityScore = Math.min(capabilities.length * 10, 40);
  const cultureReadiness = cultureScore * 20;
  const resourceReadiness = resourceScore * 20;
  const maturityScore = digitalMaturity * 20;
  
  const totalScore = capabilityScore + cultureReadiness + resourceReadiness + maturityScore;
  
  return {
    score: Math.round(totalScore),
    components: {
      capabilities: Math.round(capabilityScore),
      culture: Math.round(cultureReadiness),
      resources: Math.round(resourceReadiness),
      maturity: Math.round(maturityScore)
    }
  };
};

const getTransformationStatus = (transformationId, organizationalState) => {
  if (organizationalState.completedTransformations?.includes(transformationId)) {
    return 'completed';
  }
  if (organizationalState.activeTransformations?.includes(transformationId)) {
    return 'active';
  }
  return 'available';
};

const getLayerColor = (phase, theme = 'dark') => {
  const colors = {
    dark: {
      1: '#3b82f6', // Blue
      2: '#8b5cf6', // Purple
      3: '#10b981', // Green
      4: '#f59e0b', // Orange
      5: '#ec4899'  // Pink
    },
    light: {
      1: '#1d4ed8', // Darker Blue
      2: '#7c3aed', // Darker Purple
      3: '#059669', // Darker Green
      4: '#d97706', // Darker Orange
      5: '#db2777'  // Darker Pink
    }
  };
  
  return colors[theme][phase] || colors[theme][1];
};

const generateAIInsights = (organizationalState, performanceData, selectedTransformation = null) => {
  const insights = [];
  
  // Organizational Readiness Insight
  const readiness = calculateOrganizationalReadiness(organizationalState);
  insights.push({
    id: 'readiness_insight',
    type: 'readiness',
    title: 'Organizational Readiness Assessment',
    description: `Overall readiness: ${readiness.score}%. Key strengths: ${readiness.components.capabilities}% capabilities, ${readiness.components.resources}% resources.`,
    confidence: Math.min(95, readiness.score + 15),
    priority: readiness.score < 70 ? 'high' : 'medium',
    timestamp: new Date().toISOString()
  });
  
  // Performance Insight
  if (performanceData && performanceData.overallScore < 70) {
    insights.push({
      id: 'performance_insight',
      type: 'performance',
      title: 'Performance Improvement Opportunity',
      description: `Current performance score of ${performanceData.overallScore} indicates room for improvement. Focus on strategic and operational dimensions.`,
      confidence: 85,
      priority: 'high',
      timestamp: new Date().toISOString()
    });
  }
  
  // Transformation-specific Insight
  if (selectedTransformation) {
    const transReadiness = calculateTransformationReadiness(selectedTransformation, organizationalState);
    const transformationData = IEPMasterData.transformations[selectedTransformation.id];
    
    if (transformationData) {
      insights.push({
        id: `trans_${selectedTransformation.id}`,
        type: 'transformation',
        title: `Transformation Analysis: ${selectedTransformation.name}`,
        description: `${transReadiness.score}% readiness. ${transReadiness.dependenciesMet ? 'All dependencies met.' : 'Some dependencies pending.'} Expected impact: ${transformationData.expectedOutcomes || 'Significant value creation'}`,
        confidence: transReadiness.score,
        priority: transReadiness.score >= 70 ? 'high' : 'medium',
        timestamp: new Date().toISOString()
      });
    }
  }
  
  // Quick Wins Insight
  const availableTransformations = Object.values(IEPMasterData.transformations).filter(t => 
    !organizationalState.completedTransformations?.includes(t.id) && 
    !organizationalState.activeTransformations?.includes(t.id)
  );
  
  if (availableTransformations.length > 0) {
    const quickWins = availableTransformations
      .filter(t => t.priority === 'critical' || t.priority === 'high')
      .slice(0, 3);
    
    if (quickWins.length > 0) {
      insights.push({
        id: 'quick_wins_insight',
        type: 'opportunity',
        title: 'Recommended Quick Wins',
        description: `Consider starting: ${quickWins.map(t => t.name).join(', ')}. These transformations offer high impact with manageable complexity.`,
        confidence: 88,
        priority: 'high',
        timestamp: new Date().toISOString()
      });
    }
  }
  
  return insights;
};

const generateAIResponse = async (query, context = {}) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const responses = {
    'What should I start with?': {
      text: 'Based on your organizational readiness and current performance, I recommend starting with T1 (Quantum Foresight) followed by T2 (Executive Consciousness). This sequence builds strategic capabilities first.',
      confidence: 92,
      mode: 'strategic'
    },
    'Show me quick wins': {
      text: 'Quick wins with immediate impact: T7 (Process Intelligence) for operational efficiency, T30 (Digital Skills) for capability building, and T4 (Revenue Architecture) for financial impact.',
      confidence: 88,
      mode: 'operational'
    },
    'Analyze performance': {
      text: `Your current performance score of ${context.performanceData?.overallScore || 65} indicates moderate maturity. Key areas for improvement: strategic decision speed and innovation capacity. Focus on T1 and T13 transformations.`,
      confidence: 85,
      mode: 'analytical'
    },
    'Optimize resource allocation': {
      text: 'Based on current resource utilization, reallocate 20% of IT budget to innovation initiatives. Consider parallel execution of T7 and T30 for maximum efficiency gains.',
      confidence: 87,
      mode: 'financial'
    }
  };
  
  const defaultResponse = {
    text: `I've analyzed your query about "${query}". Based on your current transformation status and organizational readiness, I recommend focusing on foundational capabilities first, then scaling to advanced transformations.`,
    confidence: 80,
    mode: 'general'
  };
  
  return responses[query] || defaultResponse;
};

// ============ COMPONENT: TRANSFORMATION PREVIEW ============
const TransformationPreview = ({ 
  transformation, 
  organizationalState, 
  onSelect, 
  onStart,
  theme = 'dark' 
}) => {
  if (!transformation) {
    return (
      <div className={`transformation-preview ${theme}`}>
        <div className="preview-header">
          <h3>No Transformation Selected</h3>
        </div>
        <div className="preview-content">
          <p>Select a transformation from the universe to view details</p>
        </div>
      </div>
    );
  }

  const transformationData = IEPMasterData.transformations[transformation.id] || transformation;
  const readiness = calculateTransformationReadiness(transformationData, organizationalState);
  const status = getTransformationStatus(transformation.id, organizationalState);
  
  const getStatusColor = () => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'active': return '#3b82f6';
      case 'available': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <motion.div 
      className={`transformation-preview ${theme}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="preview-header">
        <div className="header-top">
          <h3>{transformationData.name}</h3>
          <div 
            className="status-badge" 
            style={{ 
              backgroundColor: getStatusColor(),
              color: 'white'
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </div>
        </div>
        <div className="header-subtitle">
          <span className="category">{transformationData.category || 'General'}</span>
          <span className="phase">Phase {transformationData.phase || 1}</span>
          <span 
            className="priority" 
            style={{ 
              backgroundColor: getPriorityColor(transformationData.priority) + '20',
              color: getPriorityColor(transformationData.priority),
              border: `1px solid ${getPriorityColor(transformationData.priority)}`
            }}
          >
            {transformationData.priority || 'Medium'}
          </span>
        </div>
      </div>
      
      <div className="preview-content">
        <p className="description">{transformationData.description || 'No description available.'}</p>
        
        <div className="metrics-grid">
          <div className="metric-item">
            <div className="metric-label">Readiness</div>
            <div className="metric-value">{readiness.score}%</div>
            <div className="metric-bar">
              <div 
                className="metric-fill" 
                style={{ 
                  width: `${readiness.score}%`,
                  backgroundColor: readiness.score >= 70 ? '#10b981' : readiness.score >= 50 ? '#f59e0b' : '#ef4444'
                }}
              />
            </div>
          </div>
          
          <div className="metric-item">
            <div className="metric-label">Duration</div>
            <div className="metric-value">{transformationData.expectedDuration || 'N/A'}</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-label">Complexity</div>
            <div className="metric-value">{transformationData.complexity || 'Medium'}</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-label">Layer</div>
            <div className="metric-value">{transformationData.layer || 'Foundation'}</div>
          </div>
        </div>
        
        {transformationData.dependencies && transformationData.dependencies.length > 0 && (
          <div className="dependencies-section">
            <h4>Dependencies</h4>
            <div className="dependencies-list">
              {transformationData.dependencies.map(depId => {
                const dep = IEPMasterData.transformations[depId];
                return dep ? (
                  <span key={depId} className="dependency-tag">
                    {dep.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
        
        {transformationData.metrics && (
          <div className="expected-outcomes">
            <h4>Key Metrics</h4>
            <div className="outcomes-grid">
              {Object.entries(transformationData.metrics).map(([key, metric]) => (
                <div key={key} className="outcome-item">
                  <div className="outcome-label">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </div>
                  <div className="outcome-value">
                    Target: {metric.target || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="preview-actions">
        <button 
          className="action-btn secondary"
          onClick={() => onSelect && onSelect(transformation)}
        >
          View Full Details
        </button>
        {status === 'available' && (
          <button 
            className={`action-btn primary ${readiness.score < 70 ? 'disabled' : ''}`}
            onClick={() => readiness.score >= 70 && onStart && onStart(transformation.id)}
            disabled={readiness.score < 70}
            style={{
              opacity: readiness.score < 70 ? 0.5 : 1,
              cursor: readiness.score < 70 ? 'not-allowed' : 'pointer'
            }}
          >
            {readiness.score >= 70 ? 'Start Transformation' : `Not Ready (${readiness.score}%)`}
          </button>
        )}
        {status === 'active' && (
          <button className="action-btn active" disabled>
            <span className="spinner"></span>
            In Progress
          </button>
        )}
        {status === 'completed' && (
          <button className="action-btn completed" disabled>
            ✓ Completed
          </button>
        )}
      </div>
    </motion.div>
  );
};

// ============ COMPONENT: AI VIEW ============
const AIView = ({ 
  organizationalState, 
  performanceData, 
  selectedTransformation,
  onStartTransformation,
  theme = 'dark'
}) => {
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponses, setAiResponses] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [activeMode, setActiveMode] = useState('strategic');
  
  const handleAIQuery = async (query) => {
    if (!query.trim()) return;
    
    setIsThinking(true);
    setAiQuery('');
    
    try {
      const response = await generateAIResponse(query, {
        organizationalState,
        performanceData,
        selectedTransformation
      });
      
      setAiResponses(prev => [{
        ...response,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        query
      }, ...prev.slice(0, 4)]);
    } catch (error) {
      console.error('AI query failed:', error);
      setAiResponses(prev => [{
        id: Date.now(),
        text: 'I encountered an error processing your query. Please try again.',
        confidence: 50,
        mode: 'error',
        timestamp: new Date().toISOString(),
        query
      }, ...prev.slice(0, 4)]);
    } finally {
      setIsThinking(false);
    }
  };
  
  const quickQuestions = [
    { text: 'What should I start with?', query: 'What should I start with?' },
    { text: 'Show me quick wins', query: 'Show me quick wins' },
    { text: 'Analyze performance', query: 'Analyze performance' },
    { text: 'Optimize resources', query: 'Optimize resource allocation' }
  ];
  
  const aiInsights = generateAIInsights(organizationalState, performanceData, selectedTransformation);
  
  return (
    <motion.div 
      className={`ai-view-container ${theme}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="ai-header">
        <div className="ai-title">
          <span className="ai-icon">🧠</span>
          <h2>Quantum AI Co-Pilot</h2>
        </div>
        <div className="ai-status">
          <div className="status-indicator active"></div>
          <span className="status-text">Online - Powered by Q* 10.0</span>
        </div>
      </div>
      
      <div className="ai-content">
        <div className="ai-sidebar">
          <div className="ai-avatar">
            <div className="avatar-glow"></div>
            <div className="avatar-core">
              <div className="core-animation"></div>
            </div>
            <div className="avatar-label">AI Assistant</div>
          </div>
          
          <div className="ai-modes">
            <h4>Analysis Modes</h4>
            {['strategic', 'operational', 'technical', 'financial'].map(mode => (
              <button
                key={mode}
                className={`ai-mode-btn ${activeMode === mode ? 'active' : ''}`}
                onClick={() => setActiveMode(mode)}
              >
                <span className="mode-icon">
                  {mode === 'strategic' ? '🎯' : 
                   mode === 'operational' ? '⚙️' : 
                   mode === 'technical' ? '💻' : '💰'}
                </span>
                <span className="mode-label">
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </span>
              </button>
            ))}
          </div>
          
          <div className="ai-stats">
            <div className="stat-item">
              <div className="stat-value">{aiInsights.length}</div>
              <div className="stat-label">Active Insights</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {Math.round(aiInsights.reduce((acc, insight) => acc + insight.confidence, 0) / (aiInsights.length || 1))}%
              </div>
              <div className="stat-label">Avg Confidence</div>
            </div>
          </div>
        </div>
        
        <div className="ai-main">
          <div className="insights-section">
            <div className="section-header">
              <h3>AI Insights</h3>
              <div className="insights-count">{aiInsights.length} insights</div>
            </div>
            
            <div className="insights-grid">
              {aiInsights.slice(0, 4).map(insight => (
                <div key={insight.id} className={`insight-card ${insight.type}`}>
                  <div className="insight-header">
                    <span className="insight-type">{insight.type}</span>
                    <span className="insight-confidence">{insight.confidence}% confidence</span>
                  </div>
                  <h4 className="insight-title">{insight.title}</h4>
                  <p className="insight-description">{insight.description}</p>
                  <div className="insight-footer">
                    <span className={`priority-badge priority-${insight.priority}`}>
                      {insight.priority} priority
                    </span>
                    {insight.type === 'transformation' && selectedTransformation && (
                      <button 
                        className="insight-action"
                        onClick={() => onStartTransformation && onStartTransformation(selectedTransformation.id)}
                      >
                        Start Now
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="conversation-section">
            <div className="section-header">
              <h3>AI Conversation</h3>
              <div className="mode-indicator">
                {activeMode.charAt(0).toUpperCase() + activeMode.slice(1)} Analysis
              </div>
            </div>
            
            <div className="conversation-history">
              {aiResponses.map(response => (
                <motion.div
                  key={response.id}
                  className="ai-response"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="response-header">
                    <span className="response-mode">{response.mode}</span>
                    <span className="response-confidence">{response.confidence}% confidence</span>
                  </div>
                  <p className="response-text">{response.text}</p>
                  <div className="response-meta">
                    <span className="response-query">Q: {response.query}</span>
                    <span className="response-time">
                      {new Date(response.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </motion.div>
              ))}
              
              {isThinking && (
                <div className="thinking-indicator">
                  <div className="thinking-dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                  <span className="thinking-text">Quantum AI analyzing...</span>
                </div>
              )}
            </div>
            
            <div className="quick-questions">
              <div className="questions-label">Quick Questions:</div>
              <div className="questions-grid">
                {quickQuestions.map((item, index) => (
                  <button
                    key={index}
                    className="quick-question"
                    onClick={() => handleAIQuery(item.query)}
                  >
                    {item.text}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="ai-input-container">
              <input
                type="text"
                className="ai-input"
                placeholder={`Ask about ${activeMode} transformations, sequencing, or outcomes...`}
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAIQuery(aiQuery)}
              />
              <button 
                className="ai-send-btn"
                onClick={() => handleAIQuery(aiQuery)}
                disabled={!aiQuery.trim() || isThinking}
              >
                {isThinking ? (
                  <>
                    <span className="send-spinner"></span>
                    Thinking...
                  </>
                ) : (
                  <>
                    <span className="send-icon">🚀</span>
                    Ask AI
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {selectedTransformation && (
        <div className="ai-recommendation">
          <div className="recommendation-header">
            <h4>🎯 AI Recommendation</h4>
            <div className="recommendation-confidence">92% confidence</div>
          </div>
          <div className="recommendation-content">
            <div className="recommended-transformation">
              <div className="transformation-name">{selectedTransformation.name}</div>
              <div className="transformation-metrics">
                <span className="metric">
                  Readiness: {calculateTransformationReadiness(selectedTransformation, organizationalState).score}%
                </span>
                <span className="metric">
                  Priority: {selectedTransformation.priority || 'High'}
                </span>
                <span className="metric">
                  Phase: {selectedTransformation.phase || 1}
                </span>
              </div>
            </div>
            <p className="recommendation-reasoning">
              This transformation aligns with your current capabilities and strategic objectives. 
              Starting now accelerates your digital maturity by 2-3x compared to alternative sequences.
            </p>
            <button 
              className="accept-recommendation-btn"
              onClick={() => onStartTransformation && onStartTransformation(selectedTransformation.id)}
            >
              <span className="btn-icon">🚀</span>
              Accept Recommendation & Start
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ============ COMPONENT: DASHBOARD VIEW ============
const DashboardView = ({ 
  organizationalState, 
  performanceData,
  theme = 'dark'
}) => {
  const transformations = IEPMasterData.transformations;
  
  const calculateMetrics = () => {
    const totalTransformations = Object.keys(transformations).length;
    const completed = organizationalState.completedTransformations?.length || 0;
    const active = organizationalState.activeTransformations?.length || 0;
    const available = totalTransformations - completed - active;
    
    const overallProgress = Math.round((completed / totalTransformations) * 100);
    
    const readiness = calculateOrganizationalReadiness(organizationalState);
    
    return {
      totalTransformations,
      completed,
      active,
      available,
      overallProgress,
      readinessScore: readiness.score,
      performanceScore: performanceData?.overallScore || 65
    };
  };
  
  const metrics = calculateMetrics();
  
  const getPerformanceTrend = () => {
    const score = performanceData?.overallScore || 65;
    if (score >= 80) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 60) return 'fair';
    return 'needs-improvement';
  };
  
  return (
    <div className={`dashboard-view-container ${theme}`}>
      <div className="dashboard-header">
        <h2>Transformation Dashboard</h2>
        <div className="dashboard-updated">
          Updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      
      <div className="dashboard-content">
        <div className="metrics-overview">
          <div className="metric-card large">
            <div className="metric-header">
              <h3>Overall Progress</h3>
              <div className={`metric-trend ${getPerformanceTrend()}`}>
                {metrics.overallProgress}% Complete
              </div>
            </div>
            <div className="metric-value">{metrics.overallProgress}%</div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${metrics.overallProgress}%` }}
              ></div>
            </div>
            <div className="metric-details">
              <span>{metrics.completed} completed</span>
              <span>{metrics.active} active</span>
              <span>{metrics.available} available</span>
            </div>
          </div>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">🎯</div>
              <div className="metric-value">{metrics.readinessScore}%</div>
              <div className="metric-label">Readiness</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-value">{metrics.performanceScore}</div>
              <div className="metric-label">Performance</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">🚀</div>
              <div className="metric-value">{metrics.active}</div>
              <div className="metric-label">Active Now</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">🏆</div>
              <div className="metric-value">{metrics.completed}</div>
              <div className="metric-label">Completed</div>
            </div>
          </div>
        </div>
        
        <div className="transformation-breakdown">
          <h3>Transformation Breakdown</h3>
          <div className="breakdown-grid">
            <div className="breakdown-card">
              <h4>By Category</h4>
              <div className="breakdown-content">
                {['strategic', 'operational', 'cultural', 'commercial', 'talent'].map(category => {
                  const count = Object.values(transformations).filter(t => t.category === category).length;
                  const completed = Object.values(transformations).filter(t => 
                    t.category === category && organizationalState.completedTransformations?.includes(t.id)
                  ).length;
                  return (
                    <div key={category} className="breakdown-item">
                      <span className="item-label">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </span>
                      <span className="item-value">
                        {completed}/{count} ({Math.round((completed/count)*100)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="breakdown-card">
              <h4>By Priority</h4>
              <div className="breakdown-content">
                {['critical', 'high', 'medium', 'low'].map(priority => {
                  const count = Object.values(transformations).filter(t => t.priority === priority).length;
                  const completed = Object.values(transformations).filter(t => 
                    t.priority === priority && organizationalState.completedTransformations?.includes(t.id)
                  ).length;
                  return (
                    <div key={priority} className="breakdown-item">
                      <span className="item-label">
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </span>
                      <span className="item-value">
                        {completed}/{count} ({Math.round((completed/count)*100)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="breakdown-card">
              <h4>By Phase</h4>
              <div className="breakdown-content">
                {[1, 2, 3, 4].map(phase => {
                  const count = Object.values(transformations).filter(t => t.phase === phase).length;
                  const completed = Object.values(transformations).filter(t => 
                    t.phase === phase && organizationalState.completedTransformations?.includes(t.id)
                  ).length;
                  return (
                    <div key={phase} className="breakdown-item">
                      <span className="item-label">Phase {phase}</span>
                      <span className="item-value">
                        {completed}/{count} ({Math.round((completed/count)*100)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        <div className="recent-activity">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {organizationalState.completedTransformations?.slice(-3).reverse().map(transformationId => {
              const transformation = transformations[transformationId];
              return transformation ? (
                <div key={transformationId} className="activity-item">
                  <div className="activity-icon">✅</div>
                  <div className="activity-content">
                    <div className="activity-title">{transformation.name} completed</div>
                    <div className="activity-time">Recently completed</div>
                  </div>
                </div>
              ) : null;
            })}
            {organizationalState.activeTransformations?.slice(-2).reverse().map(transformationId => {
              const transformation = transformations[transformationId];
              return transformation ? (
                <div key={transformationId} className="activity-item">
                  <div className="activity-icon">🔄</div>
                  <div className="activity-content">
                    <div className="activity-title">{transformation.name} in progress</div>
                    <div className="activity-time">Active now</div>
                  </div>
                </div>
              ) : null;
            })}
            {(!organizationalState.completedTransformations?.length && !organizationalState.activeTransformations?.length) && (
              <div className="activity-item">
                <div className="activity-icon">🎯</div>
                <div className="activity-content">
                  <div className="activity-title">Ready to begin your transformation journey</div>
                  <div className="activity-time">Start your first transformation</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const IEPQuantumExperience = () => {
  const { theme, toggleTheme } = useTheme();
  
  // Core State Management
  const [currentView, setCurrentView] = useState('universe');
  const [selectedTransformation, setSelectedTransformation] = useState(null);
  const [organizationalState, setOrganizationalState] = useState({
    id: 'org-default',
    name: 'Enterprise Corporation',
    industry: 'Technology',
    size: 'Large Enterprise',
    revenue: 5000000000,
    employees: 10000,
    capabilities: ['strategic_data', 'executive_commitment', 'basic_digital'],
    cultureScore: 0.7,
    resourceScore: 0.6,
    digitalMaturity: 0.65,
    activeTransformations: [],
    completedTransformations: ['T7', 'T30'],
    measurements: {}
  });
  
  const [performanceData, setPerformanceData] = useState({
    overallScore: 72,
    trend: 'improving',
    lastUpdated: new Date().toISOString(),
    dimensions: {
      strategic: 68,
      operational: 75,
      cultural: 65,
      financial: 70,
      innovation: 60
    }
  });
  
  const [aiInsights, setAiInsights] = useState([]);
  const [userProgress, setUserProgress] = useState({
    overallProgress: 15,
    completedTransformations: 2,
    activeTransformations: 0,
    milestonesCompleted: 8
  });
  
  const [showTutorial, setShowTutorial] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [notification, setNotification] = useState(null);
  
  const notificationTimeoutRef = useRef(null);
  const controls = useAnimation();

  // Initialize with IEPMasterData
  const transformations = useMemo(() => IEPMasterData.transformations, []);
  
  // ============ INITIALIZATION ============
  useEffect(() => {
    console.log('🚀 Initializing Quantum Experience with IEPMasterData...');
    
    // Start ambient animations
    controls.start({
      scale: [1, 1.02, 1],
      rotate: [0, 1, -1, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    });
    
    // Generate initial AI insights
    const initialInsights = generateAIInsights(organizationalState, performanceData, null);
    setAiInsights(initialInsights);
    
    // Calculate initial progress
    const totalTransformations = Object.keys(transformations).length;
    const completed = organizationalState.completedTransformations.length;
    const overallProgress = Math.round((completed / totalTransformations) * 100);
    
    setUserProgress(prev => ({
      ...prev,
      overallProgress,
      completedTransformations: completed,
      activeTransformations: organizationalState.activeTransformations.length
    }));
    
    // Check for achievements
    if (completed >= 2) {
      triggerAchievementCelebration({
        id: 'quick_start',
        title: 'Quick Starter',
        description: 'Completed first transformations successfully',
        reward: 'Digital Pioneer Badge',
        icon: '🚀'
      });
    }
    
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // ============ CORE EVENT HANDLERS ============
  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({ message, type });
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, duration);
  };

  const handleTransformationSelect = useCallback((transformation) => {
    if (!transformation) return;
    
    console.log('🎯 Selected:', transformation.id, transformation.name);
    setSelectedTransformation(transformation);
    
    // Generate AI insights for selected transformation
    const newInsights = generateAIInsights(organizationalState, performanceData, transformation);
    setAiInsights(newInsights);
    
    showNotification(`Selected: ${transformation.name}`, 'info');
  }, [organizationalState, performanceData]);

  const handleStartTransformation = useCallback(async (transformationId) => {
    console.log('🚀 Starting transformation:', transformationId);
    
    const transformation = transformations[transformationId];
    if (!transformation) {
      showNotification('Transformation not found!', 'error');
      return;
    }
    
    // Check if already completed
    if (organizationalState.completedTransformations.includes(transformationId)) {
      showNotification('This transformation is already completed!', 'warning');
      return;
    }
    
    // Check if already active
    if (organizationalState.activeTransformations.includes(transformationId)) {
      showNotification('This transformation is already in progress!', 'warning');
      return;
    }
    
    // Check dependencies from IEPMasterData
    const transformationData = IEPMasterData.transformations[transformationId];
    const unmetDependencies = transformationData?.dependencies?.filter(
      depId => !organizationalState.completedTransformations.includes(depId)
    ) || [];
    
    if (unmetDependencies.length > 0) {
      const depNames = unmetDependencies.map(id => {
        const dep = IEPMasterData.transformations[id];
        return dep?.name || id;
      }).join(', ');
      
      showNotification(`Complete dependencies first: ${depNames}`, 'warning');
      return;
    }
    
    // Check readiness
    const readiness = calculateTransformationReadiness(transformationData, organizationalState);
    if (readiness.score < 70) {
      showNotification(`Not ready (${readiness.score}%). Improve prerequisites first.`, 'warning');
      return;
    }
    
    // Start transformation
    setOrganizationalState(prev => ({
      ...prev,
      activeTransformations: [...prev.activeTransformations, transformationId]
    }));
    
    setUserProgress(prev => ({
      ...prev,
      activeTransformations: prev.activeTransformations + 1
    }));
    
    showNotification(`🎉 Started: ${transformation.name}!`, 'success', 5000);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      // Update progress visualization
    }, 2000);
    
    // Simulate completion after 10 seconds
    setTimeout(() => {
      clearInterval(progressInterval);
      
      setOrganizationalState(prev => ({
        ...prev,
        activeTransformations: prev.activeTransformations.filter(id => id !== transformationId),
        completedTransformations: [...prev.completedTransformations, transformationId]
      }));
      
      const completedCount = organizationalState.completedTransformations.length + 1;
      const totalCount = Object.keys(transformations).length;
      const newProgress = Math.round((completedCount / totalCount) * 100);
      
      setUserProgress(prev => ({
        ...prev,
        activeTransformations: prev.activeTransformations - 1,
        completedTransformations: completedCount,
        overallProgress: newProgress
      }));
      
      showNotification(`✅ Completed: ${transformation.name}!`, 'success', 5000);
      
      // Trigger achievement if first completion
      if (organizationalState.completedTransformations.length === 0) {
        triggerAchievementCelebration({
          id: 'first_completion',
          title: 'First Transformation Complete!',
          description: 'Successfully completed your first transformation',
          reward: 'Transformation Pioneer Badge',
          icon: '🏆'
        });
      }
      
      // Update performance
      setPerformanceData(prev => ({
        ...prev,
        overallScore: Math.min(100, prev.overallScore + 3),
        lastUpdated: new Date().toISOString()
      }));
      
      // Update AI insights
      const updatedInsights = generateAIInsights(
        {...organizationalState, completedTransformations: [...organizationalState.completedTransformations, transformationId]},
        {...performanceData, overallScore: Math.min(100, performanceData.overallScore + 3)},
        null
      );
      setAiInsights(updatedInsights);
      
    }, 10000);
    
  }, [organizationalState, transformations, performanceData]);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    showNotification(`Switched to ${view} view`, 'info');
  }, []);

  const triggerAchievementCelebration = (achievement) => {
    if (!achievement) return;
    
    setCelebrationData(achievement);
    setShowCelebration(true);
    
    setTimeout(() => {
      setShowCelebration(false);
    }, 5000);
  };

  // ============ RENDER FUNCTIONS ============
  const renderMainView = () => {
    switch(currentView) {
      case 'universe':
        return renderUniverseView();
      case 'ai':
        return (
          <AIView
            organizationalState={organizationalState}
            performanceData={performanceData}
            selectedTransformation={selectedTransformation}
            onStartTransformation={handleStartTransformation}
            theme={theme}
          />
        );
      case 'dashboard':
        return (
          <DashboardView
            organizationalState={organizationalState}
            performanceData={performanceData}
            theme={theme}
          />
        );
      default:
        return renderUniverseView();
    }
  };

  const renderUniverseView = () => {
    // Group transformations by phase from IEPMasterData
    const groupedTransformations = {};
    
    Object.values(transformations).forEach(transformation => {
      const phase = transformation.phase || 1;
      if (!groupedTransformations[phase]) {
        groupedTransformations[phase] = [];
      }
      groupedTransformations[phase].push(transformation);
    });
    
    const totalTransformations = Object.keys(transformations).length;
    const completedCount = organizationalState.completedTransformations.length;
    const activeCount = organizationalState.activeTransformations.length;
    const overallProgress = Math.round((completedCount / totalTransformations) * 100);
    
    return (
      <motion.div 
        className={`universe-view-container ${theme}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="universe-header">
          <div className="header-left">
            <h2>Quantum Universe</h2>
            <p className="header-subtitle">
              Explore {totalTransformations} transformations across {Object.keys(groupedTransformations).length} phases
            </p>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <div className="stat-value">{performanceData.overallScore}</div>
              <div className="stat-label">Performance</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{overallProgress}%</div>
              <div className="stat-label">Progress</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{activeCount}</div>
              <div className="stat-label">Active</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{completedCount}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>
        </div>
        
        <div className="universe-canvas">
          {Object.entries(groupedTransformations).map(([phase, phaseTransformations]) => (
            <div key={phase} className="transformation-layer">
              <div className="layer-header">
                <div className="layer-label" style={{ color: getLayerColor(parseInt(phase), theme) }}>
                  Phase {phase}
                </div>
                <div className="layer-count">{phaseTransformations.length} transformations</div>
              </div>
              
              <div className="layer-nodes">
                {phaseTransformations.map(transformation => {
                  const status = getTransformationStatus(transformation.id, organizationalState);
                  const phaseColor = getLayerColor(transformation.phase, theme);
                  const readiness = calculateTransformationReadiness(transformation, organizationalState);
                  
                  return (
                    <motion.div
                      key={transformation.id}
                      className={`transformation-node ${status}`}
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        borderColor: phaseColor,
                        boxShadow: status === 'active' ? `0 0 20px ${phaseColor}40` : 'none'
                      }}
                      whileHover={{ 
                        scale: 1.1, 
                        boxShadow: `0 0 30px ${phaseColor}60` 
                      }}
                      whileTap={{ scale: 0.95 }}
                      onMouseEnter={() => setHoveredNode(transformation)}
                      onMouseLeave={() => {
                        if (hoveredNode?.id === transformation.id) {
                          setHoveredNode(null);
                        }
                      }}
                      onClick={() => handleTransformationSelect(transformation)}
                    >
                      <div className="node-glow" style={{ backgroundColor: `${phaseColor}20` }} />
                      
                      <div className="node-content">
                        <div className="node-icon">
                          {status === 'completed' ? '✅' : 
                           status === 'active' ? '🔄' : 
                           transformation.priority === 'critical' ? '🔥' : '⚡'}
                        </div>
                        
                        <div className="node-name">{transformation.name}</div>
                        
                        <div className="node-meta">
                          <span className="node-priority">
                            {transformation.priority}
                          </span>
                          <span className="node-readiness" style={{ 
                            color: readiness.score >= 70 ? '#10b981' : readiness.score >= 50 ? '#f59e0b' : '#ef4444'
                          }}>
                            {readiness.score}%
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        <AnimatePresence>
          {hoveredNode && (
            <motion.div
              className="node-preview-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <TransformationPreview
                transformation={hoveredNode}
                organizationalState={organizationalState}
                onSelect={handleTransformationSelect}
                onStart={handleStartTransformation}
                theme={theme}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // ============ MAIN RENDER ============
  return (
    <div className={`iep-quantum-experience ${theme}`}>
      {/* Navigation */}
      <nav className="quantum-nav">
        <div className="nav-brand">
          <div className="brand-container">
            <span className="brand-icon">🌀</span>
            <div className="brand-text">
              <div className="brand-primary">QUANTUM IEP</div>
              <div className="brand-subtitle">Powered by IEPMasterData</div>
            </div>
          </div>
          
          <div className="engine-status">
            <div className="status-indicator online"></div>
            <span className="status-text">Quantum Engine Active</span>
          </div>
        </div>
        
        <div className="nav-views">
          {[
            { id: 'universe', label: 'Universe', icon: '🌌', description: 'Explore transformations' },
            { id: 'ai', label: 'AI Co-Pilot', icon: '🧠', description: 'Get AI recommendations' },
            { id: 'dashboard', label: 'Dashboard', icon: '📊', description: 'Performance metrics' }
          ].map(view => (
            <button
              key={view.id}
              className={`nav-view-btn ${currentView === view.id ? 'active' : ''}`}
              onClick={() => handleViewChange(view.id)}
              title={view.description}
            >
              <span className="view-icon">{view.icon}</span>
              <span className="view-label">{view.label}</span>
            </button>
          ))}
        </div>
        
        <div className="nav-tools">
          <div className="progress-display">
            <div className="progress-label">Progress</div>
            <div className="progress-value">{userProgress.overallProgress}%</div>
          </div>
          
          <div className="real-time-clock">
            <span className="clock-icon">🕐</span>
            <span className="clock-time">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="quantum-main">
        <div className="view-container">
          {renderMainView()}
        </div>
      </main>
      
      {/* Notification System */}
      <AnimatePresence>
        {notification && (
          <motion.div
            className={`notification ${notification.type}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            <div className="notification-icon">
              {notification.type === 'success' ? '✅' : 
               notification.type === 'error' ? '❌' : 
               notification.type === 'warning' ? '⚠️' : 'ℹ️'}
            </div>
            <div className="notification-message">{notification.message}</div>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Achievement Celebration */}
      <AnimatePresence>
        {showCelebration && celebrationData && (
          <AchievementCelebration
            achievement={celebrationData}
            onClose={() => setShowCelebration(false)}
            theme={theme}
          />
        )}
      </AnimatePresence>
      
      {/* Tutorial Overlay */}
      <AnimatePresence>
        {showTutorial && (
          <TutorialOverlay 
            onComplete={() => setShowTutorial(false)}
            theme={theme}
          />
        )}
      </AnimatePresence>
      
      {/* Performance Widget */}
      <motion.div 
        className="performance-widget"
        animate={controls}
      >
        <div className="widget-content">
          <div className="widget-score">{performanceData.overallScore}</div>
          <div className="widget-label">Performance</div>
          <div className={`widget-trend ${performanceData.trend}`}>
            {performanceData.trend === 'improving' ? '↗' : 
             performanceData.trend === 'declining' ? '↘' : '→'}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============ ACHIEVEMENT CELEBRATION COMPONENT ============
const AchievementCelebration = ({ achievement, onClose, theme = 'dark' }) => {
  return (
    <motion.div
      className={`achievement-celebration-overlay ${theme}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="celebration-bg"></div>
      
      <motion.div
        className="achievement-content"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 15 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="achievement-icon">{achievement.icon || '🏆'}</div>
        <h2 className="achievement-title">ACHIEVEMENT UNLOCKED!</h2>
        <div className="achievement-name">{achievement.title}</div>
        <div className="achievement-description">{achievement.description}</div>
        <div className="achievement-reward">
          <span className="reward-icon">🎁</span>
          Reward: {achievement.reward}
        </div>
        <button className="celebration-continue-btn" onClick={onClose}>
          Continue Transformation Journey
        </button>
      </motion.div>
    </motion.div>
  );
};

// ============ TUTORIAL OVERLAY ============
const TutorialOverlay = ({ onComplete, theme = 'dark' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: 'Welcome to Quantum Transformation',
      description: 'Powered by IEPMasterData, this platform guides you through 96 enterprise transformations with AI-powered insights.',
      icon: '🚀'
    },
    {
      title: 'Explore the Quantum Universe',
      description: 'Navigate through transformation nodes organized by phases. Each node represents a transformation initiative from the IEPMasterData.',
      icon: '🌌'
    },
    {
      title: 'Start Transformations',
      description: 'Click on transformation nodes to view details, then start them when ready. Transformations have dependencies and prerequisites from IEPMasterData.',
      icon: '🎯'
    },
    {
      title: 'Get AI Recommendations',
      description: 'Use the AI Co-Pilot for optimal sequencing, risk assessment, and personalized recommendations based on your organizational state.',
      icon: '🧠'
    },
    {
      title: 'Track Progress',
      description: 'Monitor your transformation journey through the dashboard. Earn achievements as you complete milestones!',
      icon: '📊'
    }
  ];
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };
  
  const handleSkip = () => {
    onComplete();
  };
  
  return (
    <motion.div
      className={`tutorial-overlay ${theme}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="tutorial-content"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="tutorial-step-indicator">
          {steps.map((_, index) => (
            <div 
              key={index} 
              className={`step-dot ${index <= currentStep ? 'active' : ''}`}
            ></div>
          ))}
        </div>
        
        <div className="tutorial-step-content">
          <div className="step-icon">{steps[currentStep].icon}</div>
          <h2 className="step-title">{steps[currentStep].title}</h2>
          <p className="step-description">{steps[currentStep].description}</p>
        </div>
        
        <div className="tutorial-actions">
          <button className="tutorial-skip-btn" onClick={handleSkip}>
            Skip Tutorial
          </button>
          <button className="tutorial-next-btn" onClick={handleNext}>
            {currentStep < steps.length - 1 ? 'Next' : 'Get Started'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============ WRAPPER COMPONENT ============
const IEPQuantumExperienceWithTheme = () => {
  return (
    <ThemeProvider>
      <IEPQuantumExperience />
    </ThemeProvider>
  );
};

export default IEPQuantumExperienceWithTheme;